import React from 'react';
import { Check } from 'lucide-react';

interface Step {
  id: number;
  title: string;
  description: string;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  completedSteps: number[];
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  completedSteps
}) => {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between relative">
        {steps.map((step, index) => {
          const isCompleted = completedSteps.includes(step.id);
          const isCurrent = currentStep === step.id;
          const isUpcoming = step.id > currentStep;
          
          return (
            <div
              key={step.id}
              className={`flex flex-col items-center relative z-10 ${
                index !== steps.length - 1 ? 'flex-1' : ''
              }`}
            >
              {/* Step Circle */}
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm
                  transition-all duration-300
                  ${isCompleted 
                    ? 'gold-gradient text-black shadow-lg' 
                    : isCurrent 
                      ? 'bg-gold-500/20 text-gold-400 border-2 border-gold-500' 
                      : 'bg-gray-700 text-gray-400 border-2 border-gray-600'
                  }
                `}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  step.id
                )}
              </div>
              
              {/* Step Info */}
              <div className="mt-3 text-center max-w-32">
                <p className={`text-sm font-medium ${
                  isCurrent ? 'text-gold-400' : isCompleted ? 'text-white' : 'text-gray-400'
                }`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {step.description}
                </p>
              </div>
              
              {/* Connecting Line */}
              {index !== steps.length - 1 && (
                <div
                  className={`
                    absolute top-5 left-1/2 w-full h-0.5 -translate-y-1/2
                    transition-all duration-300
                    ${isCompleted || (isCurrent && step.id < currentStep)
                      ? 'bg-gold-500' 
                      : 'bg-gray-600'
                    }
                  `}
                  style={{ left: '50%', width: 'calc(100% - 2.5rem)' }}
                />
              )}
            </div>
          );
        })}
        
        {/* Background Line */}
        <div className="absolute top-5 left-5 right-5 h-0.5 bg-gray-600 -translate-y-1/2" />
      </div>
    </div>
  );
};